import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Sparkles, CheckCircle2, AlertTriangle, Mail, Phone, MapPin, Calendar } from "lucide-react";
import { formatMoney } from "@/lib/currency";
import { API_BASE, formatApiError } from "@/lib/api";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }); } catch { return s; }
}

function planTotal(p) {
  const base = Number(p.price) || 0;
  const tax = base * ((Number(p.tax_percentage) || 0) / 100);
  return base + tax;
}

function planEarlyBirdTotal(p) {
  const total = planTotal(p);
  if (p.early_bird_active && Number(p.early_bird_discount_percent) > 0) {
    return total * (1 - Number(p.early_bird_discount_percent) / 100);
  }
  return total;
}

export default function PublicRenew() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [summary, setSummary] = useState(null);

  const [planId, setPlanId] = useState("");
  const [method, setMethod] = useState("UPI");
  const [paymentRef, setPaymentRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const res = await fetch(`${API_BASE}/public/renew/${token}`);
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          setErrorMsg(formatApiError(j.detail) || `Link is invalid (HTTP ${res.status})`);
        } else {
          const data = await res.json();
          setSummary(data);
          // Default-select the current plan if available; otherwise first plan
          const cur = data.current_term?.current_plan_id;
          const def = data.plans.find((p) => p.id === cur) || data.plans[0];
          if (def) setPlanId(def.id);
        }
      } catch (e) {
        setErrorMsg(e.message || "Network error");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const selectedPlan = useMemo(
    () => (summary?.plans || []).find((p) => p.id === planId),
    [summary, planId],
  );
  const amount = selectedPlan ? planEarlyBirdTotal(selectedPlan) : 0;
  const baseAmount = selectedPlan ? planTotal(selectedPlan) : 0;
  const earlyBirdSavings = selectedPlan && selectedPlan.early_bird_active ? baseAmount - amount : 0;

  const isEarlyBird = useMemo(() => {
    // Plan-level: any selected plan has early_bird_active flag from server
    if (selectedPlan?.early_bird_active) return true;
    // Fallback: generic "X days before expiry" hint
    const days = summary?.current_term?.days_remaining;
    return typeof days === "number" && days > 10;
  }, [summary, selectedPlan]);

  const submit = async () => {
    if (!planId) { toast.error("Please choose a plan"); return; }
    if (!paymentRef || paymentRef.trim().length < 4) {
      toast.error(method === "UPI" ? "Enter UPI reference / UTR number" : "Enter bank transaction reference");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/public/renew/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          new_plan_id: planId,
          payment_method: method,
          payment_amount: amount,
          upi_ref: paymentRef.trim(),
          backfill: false,
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(formatApiError(data.detail) || `HTTP ${res.status}`); return; }
      setSuccess(data);
      toast.success(`Renewal confirmed · ${data.new_start_date} → ${data.new_end_date}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ----- error state -----
  if (errorMsg) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto flex min-h-screen max-w-md items-center px-4">
          <div data-testid="public-renew-error" className="w-full rounded-sm border border-rose-200 bg-white p-8 text-center">
            <AlertTriangle className="mx-auto mb-3 text-rose-500" size={32} />
            <h1 className="text-lg font-semibold text-slate-900">Link unavailable</h1>
            <p className="mt-2 text-sm text-slate-600">{errorMsg}</p>
            <p className="mt-4 text-xs text-slate-500">Please contact your subscription office for a fresh renewal link.</p>
          </div>
        </div>
      </div>
    );
  }

  // ----- success state -----
  if (success) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto flex min-h-screen max-w-lg items-center px-4">
          <div data-testid="public-renew-success" className="w-full rounded-sm border border-emerald-200 bg-white p-8">
            <div className="flex justify-center">
              <div className="rounded-full bg-emerald-50 p-3">
                <CheckCircle2 className="text-emerald-600" size={36} />
              </div>
            </div>
            <h1 className="mt-4 text-center text-2xl font-semibold text-slate-900">Renewal confirmed</h1>
            <p className="mt-1 text-center text-sm text-slate-600">Thank you, {success.display_name}. Your subscription has been extended.</p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-sm border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">New term starts</div>
                <div className="mt-0.5 font-mono text-sm text-slate-900">{fmtDate(success.new_start_date)}</div>
              </div>
              <div className="rounded-sm border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Term ends</div>
                <div className="mt-0.5 font-mono text-sm text-slate-900">{fmtDate(success.new_end_date)}</div>
              </div>
            </div>

            <div className="mt-4 rounded-sm border border-slate-200 bg-white p-3 text-xs">
              <div className="text-slate-500">Subscriber Code</div>
              <div className="font-mono text-sm text-slate-900">{success.subscriber_code}</div>
              {success.invoice?.invoice_number && (
                <div className="mt-2 text-[11px] text-slate-500">
                  Invoice <span className="font-mono font-semibold text-slate-700">{success.invoice.invoice_number}</span>
                  {success.invoice.is_tax_invoice && <span className="ml-1 rounded-sm bg-emerald-50 px-1 py-0.5 text-[9px] text-emerald-700">GST</span>}
                </div>
              )}
            </div>

            {success.payment_id && (
              <Button
                data-testid="public-download-receipt"
                onClick={() => window.open(`${API_BASE}/public/payment/${success.payment_id}/invoice/${token}.pdf`, "_blank")}
                className="mt-4 w-full rounded-sm bg-[#002FA7] hover:bg-[#00227A]"
              >
                Download Receipt PDF
              </Button>
            )}

            <p className="mt-6 text-center text-[11px] text-slate-400">A receipt will be sent to your registered contact. You may close this window.</p>
          </div>
        </div>
      </div>
    );
  }

  // ----- loading state -----
  if (loading || !summary) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-2xl px-4 py-12">
          <Skeleton className="mb-6 h-10 w-2/3" />
          <Skeleton className="mb-3 h-32 w-full" />
          <Skeleton className="mb-3 h-48 w-full" />
        </div>
      </div>
    );
  }

  // ----- main flow -----
  const sub = summary.subscriber;
  const term = summary.current_term;
  const ctx = summary.context;

  return (
    <div data-testid="public-renew-page" className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-50 to-white pb-12">
      {/* Header strip */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4">
          {ctx.organization_logo ? (
            <img src={ctx.organization_logo} alt="" className="h-9 w-9 rounded-sm object-contain" />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#002FA7] text-xs font-bold text-white">
              {(ctx.organization || "VK").slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <div className="text-sm font-semibold text-slate-900">{ctx.organization || "Subscription Renewal"}</div>
            <div className="text-[11px] text-slate-500">{ctx.publication_house ? `${ctx.publication_house} · ` : ""}{ctx.periodical_title}</div>
          </div>
          <div className="ml-auto inline-flex items-center gap-1.5 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-medium text-emerald-700">
            <ShieldCheck size={12} /> Secure renewal link
          </div>
        </div>
      </div>

      <div className="mx-auto mt-8 max-w-3xl px-4">
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Subscription · Renewal</div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
          Renew {ctx.periodical_title || "your subscription"}
        </h1>
        <p className="mt-2 text-sm text-slate-600">Hi {sub.display_name}, choose your plan and confirm the payment reference. No login required.</p>

        {isEarlyBird && (
          <div data-testid="early-bird-banner" className="mt-4 inline-flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
            <Sparkles size={14} />
            {selectedPlan?.early_bird_active
              ? <>Early-bird unlocked · save {Number(selectedPlan.early_bird_discount_percent).toFixed(0)}% on this plan. {earlyBirdSavings > 0 ? `You save ${earlyBirdSavings.toFixed(0)} ${selectedPlan.currency || ""} today.` : ""}</>
              : <>Early-bird · {term.days_remaining} days before expiry. Lock-in today to avoid mailing gaps.</>}
          </div>
        )}

        {/* Subscriber + current term */}
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-sm border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Subscriber</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">{sub.display_name}</div>
            <div className="mt-1 font-mono text-[11px] text-slate-500">{sub.subscriber_code}</div>
            <div className="mt-3 space-y-1 text-xs text-slate-600">
              {sub.mobile_masked && <div className="flex items-center gap-2"><Phone size={12} className="text-slate-400" />{sub.mobile_masked}</div>}
              {sub.email_masked && <div className="flex items-center gap-2"><Mail size={12} className="text-slate-400" />{sub.email_masked}</div>}
              {(sub.city || sub.state) && <div className="flex items-center gap-2"><MapPin size={12} className="text-slate-400" />{[sub.city, sub.state].filter(Boolean).join(", ")}</div>}
            </div>
          </div>
          <div className="rounded-sm border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Current term</div>
            <div className="mt-1 text-sm text-slate-900">{term.current_plan_name || "—"}</div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Started</div>
                <div className="font-mono text-xs text-slate-700">{fmtDate(term.start_date)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Ends</div>
                <div className="font-mono text-xs text-slate-700">{fmtDate(term.end_date)}</div>
              </div>
            </div>
            {typeof term.days_remaining === "number" && (
              <div className={`mt-3 inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-[11px] ${term.days_remaining < 0 ? "bg-rose-50 text-rose-700" : term.days_remaining <= 7 ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}>
                <Calendar size={11} />
                {term.days_remaining >= 0 ? `${term.days_remaining} days remaining` : `Expired ${Math.abs(term.days_remaining)} days ago`}
              </div>
            )}
          </div>
        </div>

        {/* Plan selector */}
        <div className="mt-6 rounded-sm border border-slate-200 bg-white p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Choose plan</div>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {summary.plans.length === 0 && (
              <div className="col-span-3 rounded-sm border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
                No active plans available right now. Please contact subscription office.
              </div>
            )}
            {summary.plans.map((p) => {
              const baseTotal = planTotal(p);
              const ebTotal = planEarlyBirdTotal(p);
              const isSelected = planId === p.id;
              const hasEB = p.early_bird_active && Number(p.early_bird_discount_percent) > 0;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`plan-card-${p.id}`}
                  onClick={() => setPlanId(p.id)}
                  className={`text-left rounded-sm border p-3 transition ${isSelected ? "border-[#002FA7] bg-[#002FA7]/5 ring-1 ring-[#002FA7]" : "border-slate-200 bg-white hover:border-slate-400"}`}
                >
                  <div className="text-sm font-semibold text-slate-900">{p.plan_name}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{p.total_months} month{p.total_months === 1 ? "" : "s"}</div>
                  {hasEB ? (
                    <div className="mt-2">
                      <div className="font-mono text-base text-emerald-700">{formatMoney(ebTotal, p.currency)}</div>
                      <div className="font-mono text-[11px] text-slate-400 line-through">{formatMoney(baseTotal, p.currency)}</div>
                      <div className="mt-0.5 inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800" data-testid={`plan-early-bird-${p.id}`}>
                        <Sparkles size={9} /> Early-bird · save {Number(p.early_bird_discount_percent).toFixed(0)}%
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 font-mono text-base text-slate-900">{formatMoney(baseTotal, p.currency)}</div>
                  )}
                  {p.tax_percentage > 0 && (
                    <div className="text-[10px] text-slate-500">incl. {p.tax_percentage}% tax</div>
                  )}
                  {p.grace_period_days > 0 && (
                    <div className="mt-1 text-[10px] text-emerald-700">+{p.grace_period_days}d grace</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Payment ref */}
        {summary.plans.length > 0 && (
          <div className="mt-6 rounded-sm border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Payment</div>
            <p className="mt-1 text-xs text-slate-500">
              Pay <span className="font-mono font-semibold text-slate-700">{selectedPlan ? formatMoney(amount, selectedPlan.currency) : "—"}</span> via UPI or Bank Transfer to the subscription office, then enter the reference below to confirm renewal.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="public-method-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{method === "UPI" ? "UPI Ref / UTR" : "Bank Transaction Ref"} <span className="text-rose-600">*</span></Label>
                <Input
                  data-testid="public-payment-ref"
                  placeholder={method === "UPI" ? "12-digit UTR" : "Bank reference no."}
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  className="rounded-sm font-mono"
                />
              </div>
            </div>
            <Button
              data-testid="public-confirm-renewal"
              onClick={submit}
              disabled={submitting || !planId}
              className="mt-4 rounded-sm bg-[#002FA7] hover:bg-[#00227A]"
            >
              {submitting ? "Confirming…" : selectedPlan ? `Confirm renewal · ${formatMoney(amount, selectedPlan.currency)}` : "Confirm renewal"}
            </Button>
            <p className="mt-3 text-[11px] text-slate-400">
              By confirming, you authorise the subscription office to extend your subscription based on the verified payment reference above.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
